import type { IUser } from "@/domains/shared/models/IUser";
import { Roles, type UserRole } from "@/domains/shared/models/roles";
import { resolveAnalyticsPlanSlug } from "./resolveAnalyticsPlanSlug";

const APP_USER_ROLES: ReadonlySet<string> = new Set([
  Roles.Admin,
  Roles.Therapist,
  Roles.Parent,
  Roles.Secretary,
]);

const CLINIC_ROLE_ALLOWLIST: ReadonlySet<string> = new Set([
  "ClinicManager",
  "Therapist",
  "Secretary",
  "Finance",
]);

const LOCALE_ALLOWLIST: ReadonlySet<string> = new Set(["pt-BR", "en", "es", "de"]);

export type ProductIdentifyProperties = {
  role: UserRole;
  clinic_roles?: string[];
  plan_slug?: string;
  clinic_id?: string;
  preferred_locale?: string;
  time_zone_id?: string;
};

function normalizePreferredLocale(raw: string | null | undefined): string | undefined {
  if (!raw?.trim()) {
    return undefined;
  }

  const trimmed = raw.trim();
  if (trimmed.toLowerCase() === "pt-br") {
    return "pt-BR";
  }

  if (LOCALE_ALLOWLIST.has(trimmed)) {
    return trimmed;
  }

  return undefined;
}

/**
 * Canonical identify props only (api-contract §2 / spec §3.3).
 * Never email/name/plan/org_id; never linkedTherapistSubscriptionPlan as plan_slug.
 */
export function buildIdentifyProperties(user: IUser): ProductIdentifyProperties {
  const role: UserRole = APP_USER_ROLES.has(user.role) ? user.role : Roles.Therapist;

  const props: ProductIdentifyProperties = { role };

  const clinicRoles = (user.clinicRoles ?? []).filter((r) => CLINIC_ROLE_ALLOWLIST.has(r));
  if (clinicRoles.length > 0) {
    props.clinic_roles = clinicRoles;
  }

  if (user.activeClinicId?.trim()) {
    props.clinic_id = user.activeClinicId.trim();
  }

  // Interim FE mirror (OD-4): subscriptionPlan only — never linkedTherapistSubscriptionPlan (E-16).
  const planSlug = resolveAnalyticsPlanSlug(user.subscriptionPlan);
  if (planSlug) {
    props.plan_slug = planSlug;
  }

  const locale = normalizePreferredLocale(user.preferredLocale);
  if (locale) {
    props.preferred_locale = locale;
  }

  if (user.timeZoneId?.trim()) {
    props.time_zone_id = user.timeZoneId.trim();
  }

  return props;
}
